import React, { JSX } from 'react';
import { SearchCardProps } from '@/components/card/types';
import Icon from '@/components/icon/Icon';
import { Badge } from '@/components/badge/Badge';

const SearchCard: React.FC<SearchCardProps> = React.memo(
  ({
    title,
    description,
    type,
    category,
    date,
    className,
    variantBadge,
    breadcrumbPaths,
    query,
    ...rest
  }) => {
    const getHighlightedTitle = (
      title: string,
      query: string = '',
      wordLimit = 5,
    ): JSX.Element => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) return <>{title}</>;

      const words = title.split(' ').slice(0, wordLimit);
      const truncated = words.join(' ');
      const isTruncated = title.split(' ').length > wordLimit;

      const regex = new RegExp(`(${trimmedQuery})`, 'gi'); // match query anywhere, case-insensitive
      const parts = truncated.split(regex); // split into text and matched query parts

      return (
        <>
          {parts.map((part, index) =>
            part.toLowerCase() === trimmedQuery.toLowerCase() ? (
              <strong key={index} className="font-semibold">
                {part}
              </strong>
            ) : (
              <span key={index}>{part}</span>
            ),
          )}
          {isTruncated && '...'}
        </>
      );
    };

    // ✅ Set icon dynamically based on `type`
    const iconName =
      type === 'artikel'
        ? 'pen-tool'
        : type === 'new'
          ? 'newspaper'
          : type === 'flash-talk'
            ? 'zap'
            : type === 'podcast'
              ? 'mic'
              : type === 'webinar'
                ? 'video'
                : type === 'ratgeber'
                  ? 'book-open'
                  : type === 'news'
                    ? 'newspaper'
                    : type === 'edu-talk'
                      ? 'graduation-cap'
                      : type === 'page'
                        ? 'file'
                        : 'file'; // Default fallback

    return (
      <div className="search-card flex flex-row items-center justify-between gap-4 rounded-xl border border-neutral-100 p-4 transition-colors duration-200 ease-in-out hover:bg-neutral-0">
        <div className="flex items-center">
          {type && (
            <div className="mr-3 rounded-xl border border-neutral-100 p-3">
              <Icon name={iconName} color="#07314C" width={16} />
            </div>
          )}

          <div className="flex flex-col">
            <div className="card-header flex flex-row items-center justify-start">
              <div className="category align-center flex justify-center gap-1 text-sm">
                <span className="text-[12px] font-medium uppercase sm:text-sm">
                  {type === 'page' ? 'Info-Seite' : type}
                </span>
                {date && (
                  <Icon
                    className="my-auto"
                    name="dot"
                    width={12}
                    height={12}
                    color="rgba(128, 136, 151, 1)"
                  />
                )}
                <div className="date font-medium text-neutral-300">{date}</div>
              </div>
              {category && (
                <Icon
                  name="dot"
                  width={12}
                  height={12}
                  color="rgba(128, 136, 151, 1)"
                  className="hidden sm:block"
                />
              )}
              {category && (
                <Badge
                  variant={variantBadge ?? 'cream'}
                  title={category}
                  classNameBadge="hidden sm:block"
                />
              )}
            </div>
            <div className="card-content flex flex-col gap-2">
              <h3 className="text-sm font-normal text-neutral-700 md:text-[18px]">
                {title ? getHighlightedTitle(title, query) : null}
              </h3>

              {description &&
                (Array.isArray(description) ? (
                  <div className="description mt-2 text-sm text-gray-600">
                    {description.map((desc, index) => (
                      <p key={index}>
                        {desc.children.map((child, idx) => (
                          <span key={idx}>{child.text}</span>
                        ))}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">{description}</p>
                ))}
            </div>
          </div>
        </div>
        <Icon name="chevron-right" width={32} />
      </div>
    );
  },
);

SearchCard.displayName = 'SearchCard';

export default SearchCard;
